import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import OffCanvas from "./offCanvas";
import logoIcon from "../assets/img/Spire360_logo.svg";
import logo from "../assets/img/S360-logo.svg";
import { HeaderSearch } from "./HeaderSearch";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import Datetime from "./Datetime";
import Practice from "./practice";
import { createUsersApi } from "../api-helpers/UserApiHelper";
import { UserContextHolder } from "../auth/UserContextHolder";
import { createPartnerApi } from "../api-helpers/PatientApiHelper";
import { Partner } from "patients-experience-client";
import { LoadingHelper } from "./loader/LoadingHelper";
import NotificationsButton from "../notifications/NotificationsButton";
import IsDesktop from "../helper/CheckDevice";
import useLoggedInAccount from "../auth/LoggedInAccountInfo";
import useProfilePicture from "../hooks/useProfilePicture";
import { useDispatch } from "react-redux";
import { partnerSelected } from "../store/slices/userSlice";

const TooltipWrapper = ({
  msg = "Patients",
  value,
}: {
  msg?: string;
  value: string | number;
}) => {
  return (
    <OverlayTrigger
      trigger={["hover", "focus"]}
      placement="right"
      overlay={<Tooltip>{msg}</Tooltip>}
    >
      <span className="rounded-pill px-2 number-badge">{value}</span>
    </OverlayTrigger>
  );
};

const Header = () => {
  const usersApi = createUsersApi();
  const partnerApi = createPartnerApi();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [testPartners, setTestPartners] = useState<Partner[]>([]);
  const [allPartnersPatientsCount, setAllPartnersPatientsCount] =
    useState<number>(0);
  const [otherPartners, setOtherPartners] = useState<Partner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<{
    id: string;
    name: string;
  }>();
  const [offCanvasComp, setOffCanvasComp] = useState("");
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [menuClose, setMenuClose] = useState(false);
  const [offCanvasTitle, setOffCanvasTitle] = useState("");

  const { instance, loggedInAccount } = useLoggedInAccount();
  const { profilePictureUrl } = useProfilePicture(loggedInAccount, instance);
  const { patientId } = useParams<{ patientId: string }>();

  const dispatch = useDispatch();

  const isGatewayEnabled = process.env.REACT_APP_CONFIG_GATEWAY === "true";

  const handleReload = () => {
    setTimeout(() => {
      if (patientId) {
        window.location.href = "/";
      } else {
        window.location.reload();
      }
    }, 1000);
  };

  const getPartners = async () => {
    LoadingHelper.showLoader("main");
    try {
      const partnersResponse =
        await partnerApi.partnerControllerGetAllPartners(true);
      const selectedPartnerResponse =
        await usersApi.usersControllerGetUserSelectPartners();

      // const partner = []

      if (selectedPartnerResponse.data.partnerIds.length === 0) {
        setSelectedPartner({ id: "", name: "all" });
        dispatch(partnerSelected({ id: "", name: "all" }));
      }

      if (selectedPartnerResponse.data.partnerIds.length > 1) {
        setPartners([]);
        setSelectedPartner({ id: "", name: "all" });
        dispatch(partnerSelected({ id: "", name: "all" }));
      } else {
        setPartners(partnersResponse.data);
        const partner = partnersResponse.data.find(
          (partner) =>
            partner.partnerId === selectedPartnerResponse.data.partnerIds[0],
        );
        if (partner) {
          setSelectedPartner({ id: partner?.partnerId, name: partner.name });
          dispatch(
            partnerSelected({ id: partner?.partnerId, name: partner.name }),
          );
        }
      }

      // filter objects in partners in which partnerName contains 'test'
      const testPartners = partnersResponse.data.filter(
        (partner) => partner.isTest,
      );
      setTestPartners(testPartners);

      const nonTestPartners = partnersResponse.data.filter(
        (partner) => !partner.isTest,
      );

      const totalPatientsCount = nonTestPartners.reduce(
        (total, partner) => total + (partner.patientsCount || 0),
        0,
      );
      setAllPartnersPatientsCount(totalPatientsCount);

      // filter objects in partners in which partnerName does not contain 'test'
      const otherPartners = partnersResponse.data.filter(
        (partner) => !partner.isTest,
      );
      setOtherPartners(otherPartners);

      // Set all partners
      setPartners(partnersResponse.data);
    } catch (error) {
      console.log(error);
    } finally {
      LoadingHelper.hideLoader("main");
    }
  };

  const handleChangePartner = (partnerId: string) => {
    LoadingHelper.showLoader(".main");
    if (partnerId === "all") {
      setSelectedPartner({ id: "", name: "all" });
      const allPartners = partners
        .filter((partner) => !partner.isTest)
        .map((partner) => partner.partnerId);
      console.clear();
      console.table(allPartners);
      try {
        usersApi.usersControllerSelectPartners({ partnerIds: allPartners });
      } catch (err) {
        console.log(err);
      } finally {
        LoadingHelper.hideLoader(".main");
        handleReload();
      }
      return;
    }

    try {
      usersApi.usersControllerSelectPartners({ partnerIds: [partnerId] });
      const partnerName =
        partners?.find((partner) => partner.partnerId === partnerId)?.name ||
        "";
      setSelectedPartner({ id: partnerId, name: partnerName });
    } catch (error) {
      console.log(error);
    } finally {
      handleReload();
      // LoadingHelper.hideLoader('.main')
    }
  };

  useEffect(() => {
    if (loggedInAccount) {
      usersApi
        .usersControllerFindOne(loggedInAccount.username)
        .then((response) => UserContextHolder.init(response.data));
    }
  }, [loggedInAccount]);

  useEffect(() => {
    getPartners();
    // getSelectedPartner()
  }, []);

  const menuClick = () => {
    document.body.classList.toggle("menu-close");
    setMenuClose(!menuClose);
  };

  const handleOffCanvasShow = (comp: string) => {
    // if (comp === "services") {
    //   setOffCanvasTitle("Select Practice/Provider");
    // }
    if (comp === "notifications") {
      setOffCanvasTitle("Notifications");
    }
    setOffCanvasComp(comp);
    setOffCanvasShow(!offCanvasShow);
  };

  const handleLogout = () => {
    // Clear local and session storage, but keep grid column state if
    Object.keys(localStorage)
      .filter((key) => !key.startsWith("grid-column-state-"))
      .forEach((key) => localStorage.removeItem(key));
    sessionStorage.clear();

    // Optionally clear account state if it's stored in your app's state
    instance
      .logoutRedirect({
        postLogoutRedirectUri: "/", // Redirect to the home page after logout
      })
      .then(() => {
        // Optionally force a refresh to ensure the app is cleared
        window.location.reload();
      })
      .catch((error) => {
        console.error("Logout failed", error);
      });
  };

  const Partners = (
    <Dropdown>
      <Dropdown.Toggle
        bsPrefix={" "}
        id="dropdown-autoclose-true"
        variant={" "}
        className={`menu__plan ${IsDesktop() ? "" : "w-100"}`}
      >
        <span className="d-block fs-12 text-primary fw-medium text-nowrap py-0-375 pe-2 me-1 border-end text-start">
          {selectedPartner?.name === "all"
            ? "All Partners"
            : selectedPartner?.name}
        </span>
        <span className="material-symbols-outlined menu__plan-icon">
          keyboard_arrow_down
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="rounded-4 rounded-top overflow-hidden mt-1 border-1 fs-13"
        align={"end"}
      >
        <Dropdown.Item className="p-0">
          <Link
            to="javascript:void(0)"
            className="dropdown-item w-full"
            onClick={() => handleChangePartner("all")}
          >
            <span className="d-flex justify-content-between gap-2 align-items-center w-100">
              <span>All Partners</span>
              <TooltipWrapper value={allPartnersPatientsCount} />
            </span>
          </Link>
        </Dropdown.Item>

        {otherPartners.length > 0 && (
          <div className="mb-2 border-strike me-2">
            <span className="fs-11 bg-white px-2 position-relative z-index-1">
              Partners
            </span>
          </div>
        )}

        {otherPartners.map((partner) => {
          return (
            <Dropdown.Item key={partner.partnerId} className="p-0">
              <Link
                to="javascript:void(0)"
                className="dropdown-item w-full"
                onClick={() => {
                  handleChangePartner(partner.partnerId);
                }}
              >
                <span className="d-flex justify-content-between gap-2 align-items-center w-100">
                  <span>{partner.name}</span>
                  <TooltipWrapper value={partner.patientsCount} />
                </span>
              </Link>
            </Dropdown.Item>
          );
        })}

        {testPartners.length > 0 && (
          <div className="mb-2 border-strike me-2">
            <span className="fs-11 bg-white px-2 position-relative z-index-1">
              Test Partners
            </span>
          </div>
        )}
        {testPartners.length > 0 &&
          testPartners.map((partner) => {
            return (
              <Dropdown.Item key={partner.partnerId} className="p-0">
                <Link
                  to="javascript:void(0)"
                  className="dropdown-item w-full"
                  onClick={() => {
                    handleChangePartner(partner.partnerId);
                  }}
                >
                  <span className="d-flex justify-content-between align-items-center w-100">
                    <span>{partner.name}</span>
                    <TooltipWrapper value={partner.patientsCount} />
                  </span>
                </Link>
              </Dropdown.Item>
            );
          })}
      </Dropdown.Menu>
    </Dropdown>
  );

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };
  return (
    <>
      <header className="header" style={{ zIndex: "999" }}>
        <div>
          <Link to="/" className="header__logo">
            <img src={logo} className="header__logo-img img-fluid" />
            <img src={logoIcon} className="header__logo-icon img-fluid" />
          </Link>
        </div>
        <div className="d-flex flex-fill align-items-center">
          <div>
            <a
              className="menu__link me-2 me-md-0"
              href="#"
              onClick={() => menuClick()}
            >
              <span className="material-symbols-outlined menu__icon fs-26 px-lg-2">
                <span className="menu-close--show">menu</span>
                <span className="menu-close--hide">menu_open</span>
              </span>
            </a>
          </div>
          <Datetime date={new Date()} />
          <div className="flex-fill">
            <HeaderSearch></HeaderSearch>
          </div>
          <div className="px-2">
            <ul className="menu">
              {IsDesktop() && <li className="menu__item mx-2">{Partners}</li>}
              <NotificationsButton></NotificationsButton>
              <li className="menu__item">
                <Dropdown>
                  <Dropdown.Toggle
                    bsPrefix={" "}
                    id="dropdown-autoclose-true"
                    variant={" "}
                    className="menu__user"
                  >
                    <span className="user-icon">
                      <img
                        src={
                          profilePictureUrl ||
                          "https://cdn-icons-png.flaticon.com/512/168/168719.png"
                        }
                        alt={loggedInAccount?.name || "User"}
                        className="img-fluid"
                      />
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className="rounded-4 rounded-top"
                    align={"end"}
                  >
                    <div className="px-6 py-3 border-bottom">
                      <strong>
                        {isGatewayEnabled ? loggedInAccount?.name : "User-1"}
                      </strong>
                      <div className="fs-12 text-light text-truncate">
                        {isGatewayEnabled
                          ? loggedInAccount?.username
                          : "user-1@indoqubix.com"}
                      </div>
                    </div>
                    {!IsDesktop() && (
                      <div className="dropdown-item border-bottom">
                        {Partners}
                      </div>
                    )}
                    <div className="pt-2 pb-4">
                      <Link
                        to="javascript:void(0)"
                        className="dropdown-item"
                        onClick={() => toggleFullscreen()}
                      >
                        <span className="material-symbols-outlined fs-20 align-text-top me-1">
                          zoom_out_map
                        </span>{" "}
                        Screen Maximize
                      </Link>
                      <button className="dropdown-item" onClick={handleLogout}>
                        <span className="material-symbols-outlined fs-20 align-text-top me-1">
                          logout
                        </span>{" "}
                        Logout
                      </button>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </li>
            </ul>
          </div>
        </div>
      </header>
      <OffCanvas
        title={offCanvasTitle}
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={setOffCanvasShow}
        offCanvasComp={offCanvasComp}
      >
        {offCanvasComp === "services" && <Practice />}
        {offCanvasComp === "help" && (
          <div>
            <div className="fs-13 mb-2">
              <a href="#" className="a-link text-primary">
                Live Chat{" "}
              </a>
            </div>
            <div className="fs-13 mb-2">
              <a href="#" className="a-link text-primary">
                Create A Support Request{" "}
              </a>
            </div>
            <div className="fs-13 mb-2">
              <a href="#" className="a-link text-primary">
                Clinical Escalation Form{" "}
              </a>
            </div>
          </div>
        )}
      </OffCanvas>
    </>
  );
};
export default Header;
